import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { motion } from 'framer-motion';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useForm, Controller } from 'react-hook-form';
import { ContactFormData } from '../../types';
import { COMPANY_INFO } from '../../utils/constants';
import { sendContactEmail, initEmailJS } from '../../utils/emailjs';

const ContactSection: React.FC = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Initialiser EmailJS au chargement du composant
  React.useEffect(() => {
    initEmailJS();
  }, []);

  // Honeypot state (champ caché anti-bot)
  const [honeypot, setHoneypot] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      // Envoyer l'email avec le honeypot
      await sendContactEmail({ ...data, website: honeypot });
      
      setSnackbarMessage('Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      reset();
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setSnackbarMessage('Une erreur est survenue. Veuillez réessayer ou nous contacter directement.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 30 }} />,
      title: 'Email',
      content: 'contact@eztech-solutions.fr',
      link: 'mailto:contact@eztech-solutions.fr',
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 30 }} />,
      title: 'Adresse',
      content: `${COMPANY_INFO.address.street}\n${COMPANY_INFO.address.postalCode} ${COMPANY_INFO.address.city}`,
    },
  ];

  return (
    <Box
      id="contact"
      sx={{
        py: 10,
        backgroundColor: 'background.paper',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Contact
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
          >
            Parlons de votre projet et trouvons ensemble la meilleure solution
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 4 }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Contactez-nous
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Nous sommes à votre écoute pour discuter de vos besoins et vous proposer les meilleures solutions.
              </Typography>

              {contactInfo.map((info, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    backgroundColor: 'background.default',
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      mr: 2,
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                    }}
                  >
                    {info.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {info.title}
                    </Typography>
                    {info.link ? (
                      <Typography
                        component="a"
                        href={info.link}
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          textDecoration: 'none',
                          '&:hover': { color: 'primary.main' },
                        }}
                      >
                        {info.content}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {info.content}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </motion.div>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  backgroundColor: 'background.default',
                }}
              >
                <form onSubmit={handleSubmit(onSubmit)}>
                  {/* Honeypot field - hidden from users, filled by bots */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      opacity: 0,
                      height: 0,
                      width: 0,
                      zIndex: -1,
                    }}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{ required: 'Le nom est requis' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Nom complet"
                            variant="outlined"
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Controller
                        name="email"
                        control={control}
                        rules={{
                          required: 'L\'email est requis',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Email invalide',
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email"
                            variant="outlined"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                          />
                        )}
                      />
                    </Grid>
                   <Grid size={{ xs: 12 }}>
                      <Controller
                        name="subject"
                        control={control}
                        rules={{ required: 'Le sujet est requis' }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Sujet"
                            variant="outlined"
                            error={!!errors.subject}
                            helperText={errors.subject?.message}
                          />
                        )}
                      />
                    </Grid>
                   <Grid size={{ xs: 12 }}>
                      <Controller
                        name="message"
                        control={control}
                        rules={{
                          required: 'Le message est requis',
                          minLength: {
                            value: 10,
                            message: 'Le message doit contenir au moins 10 caractères',
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Message"
                            variant="outlined"
                            multiline
                            rows={6}
                            error={!!errors.message}
                            helperText={errors.message?.message}
                          />
                        )}
                      />
                    </Grid>
                   <Grid size={{ xs: 12 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        endIcon={<SendIcon />}
                        disabled={isSubmitting}
                        sx={{ minWidth: 200 }}
                      >
                        {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ContactSection;